/**
 * SvmShared - the small presentational pieces of the SVM checklist flow:
 * ErrorBanner, LoadingNote, Breadcrumb, ProgressBar, the SyncedAgo indicator
 * and the failure Toast. All module level, inline styles only.
 */
import { useEffect, useState } from 'react';
import { BTN, CARD, GREEN, LINKISH, pctOf, relTime } from './svmModel';

export function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{ ...CARD, borderColor: '#FFCDD2', background: '#FFEBEE', display: 'flex', alignItems: 'center', gap: 14 }}>
      <span style={{ fontSize: 12.5, color: '#B71C1C', fontWeight: 600, flex: 1 }}>{message || 'Something went wrong.'}</span>
      {onRetry && (
        <button type="button" style={{ ...BTN, background: '#B71C1C', color: '#fff' }} onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function LoadingNote({ text }) {
  return <div style={{ padding: 40, textAlign: 'center', color: '#888', fontSize: 13 }}>{text || 'Loading…'}</div>;
}

export function Breadcrumb({ parts }) {
  return (
    <div style={{ fontSize: 12, color: '#888', marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {parts.map((p, i) => (
        <span key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {i > 0 && <span style={{ color: '#CCC' }}>/</span>}
          {p.onClick ? (
            <button type="button" style={{ ...LINKISH, color: GREEN, fontWeight: 700, fontSize: 12 }} onClick={p.onClick}>
              {p.label}
            </button>
          ) : (
            <span style={{ fontWeight: 700, color: '#0D0D0D' }}>{p.label}</span>
          )}
        </span>
      ))}
    </div>
  );
}

export function ProgressBar({ signed, total, height = 8 }) {
  const pct = pctOf(signed, total);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height, background: '#EEE', borderRadius: height, overflow: 'hidden' }}>
        <div
          style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: GREEN, borderRadius: height, transition: 'width .3s' }}
        />
      </div>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#555', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {signed}/{total} · {pct}%
      </span>
    </div>
  );
}

// "Synced Xs ago" - its own ticker so the big checklist tree doesn't re-render.
export function SyncedAgo({ lastSync }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(iv);
  }, []);
  if (!lastSync) return <span style={{ fontSize: 10.5, color: '#AAA' }}>Syncing…</span>;
  return <span style={{ fontSize: 10.5, color: '#AAA', whiteSpace: 'nowrap' }}>Synced {relTime(lastSync)}</span>;
}

// Bottom-centre transient toast (a failed optimistic tick reverting).
export function Toast({ text }) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1100,
        background: '#212121',
        color: '#fff',
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 8,
        padding: '10px 18px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {text}
    </div>
  );
}
